"use client";

import { Minus, Plus } from "lucide-react";
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const faqData = [
  {
    value: "participation-and-contribution",
    title: "Participation and Contribution",
    faqs: [
      {
        id: "pc-1",
        question: "How can individuals contribute or volunteer?",
        answer:
          "You can adopt trees, volunteer at nurseries, join or organize plantation drives, identify land, support fundraising, and help monitor saplings.​",
      },
      {
        id: "pc-2",
        question: "How to start a plantation drive near you?",
        answer:
          "Secure land permissions, gather resources, source saplings (preferably from Forests by Heartfulness nurseries), prepare the site, plant, and water regularly. Forests by Heartfulness can offer guidance.​",
      },
      {
        id: "pc-3",
        question: "Can companies or institutions partner with Forests by Heartfulness for CSR projects?",
        answer:
          "Yes, we work extensively with CSR partners to fund plantation projects, both for implementation and aftercare.",
      },
    ],
  },
  {
    value: "plantation-and-monitoring",
    title: "Plantation and Monitoring",
    faqs: [
      {
        id: "pm-1",
        question: "Where and when are trees planted?",
        answer:
          "Plantations happen during monsoon and late winter on farms, public lands, and homes. Each sapling is geo-tagged for tracking.​",
      },
      {
        id: "pm-2",
        question: "How are saplings monitored after planting?",
        answer:
          "Forests by Heartfulness monitors saplings with assigned caregivers and local NGO support, tracking their growth for at least a year. An app is being developed for geo-tagging.​",
      },
      {
        id: "pm-3",
        question: "What happens if a planted tree dies?",
        answer:
          "When working with nature, sometimes a few plants do not survive despite our best efforts. We do a root cause analysis and replant a new sapling within 2 months to ensure the forest has the same number of saplings as originally planned.",
      },
      {
        id: "pm-4",
        question: "Which tree species are planted, and why those?",
        answer:
          "Our species selection methodology is based on the needs of the local ecosystem and the community needs. While we plant 100% native saplings, we take specific care to ensure the species can survive in the local conditions (drought prone, flood prone etc).",
      },
      {
        id: "pm-5",
        question: "How long does aftercare/maintenance continue after planting?",
        answer:
          "Typically 3-5 years. A native sapling takes about 3 years to establish itself well. Thereafter, it is just about protecting them from anthropogenic interference and ensuring removal of invasive species on a periodic basis.",
      },
      {
        id: "pm-6",
        question: "Can I visit the site where my tree was planted?",
        answer:
          "If you have planted a geo-tagged tree, yes you can visit the place where your sapling is planted. You can do it through the “Dashboard” option after logging into your account.",
      },
    ],
  },
  {
    value: "donation-and-impact",
    title: "Plantation and Impact",
    faqs: [
      {
        id: "di-1",
        question: "Can donors select tree species or location?",
        answer:
          "Forests by Heartfulness chooses species based on local ecology. Donors can't pick an exact tree or spot but may support specific drives as options develop.​",
      },
      {
        id: "di-2",
        question: "Are donations tax-deductible?",
        answer:
          "Yes, donations are eligible for tax benefits under Forests by Heartfulness's charitable status.​",
      },
    ],
  },
];

const FAQs = () => {
  const [openItem, setOpenItem] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(faqData[0].value);

  const toggleItem = (itemId: string) => {
    setOpenItem(openItem === itemId ? null : itemId);
  };
  return (
    <main className="max-w-7xl mx-auto md:mt-16 mt-8 px-4 md:px-8 overflow-hidden">
      <h2 className="text-2xl sm:text-[32px] font-[Playfair_Display] font-semibold sm:text-center text-[#232D26] mb-6">
        FAQs
      </h2>
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="md:hidden mb-6 space-y-1.5">
          <h3 className="text-[#19212C] font-medium text-sm leading-5">
            Category
          </h3>
          <Select value={activeTab} onValueChange={setActiveTab}>
            <SelectTrigger className="w-full min-h-12 hover:rounded-[8px] border-[#95AAD5] rounded-[8px] text-[#003399] font-bold">
              <SelectValue placeholder="Select category" />
            </SelectTrigger>
            <SelectContent className="rounded-[8px] hover:rounded-[8px]">
              {faqData.map((tab) => (
                <SelectItem key={tab.value} value={tab.value}>
                  {tab.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="hidden md:block w-fit mx-auto">
          <TabsList className="flex bg-transparent p-0 h-auto w-full justify-start">
            {faqData.map((tab) => (
              <TabsTrigger
                key={tab.value}
                value={tab.value}
                className="flex items-center px-5 py-4 border-b-[2px] bg-transparent border-[#B7B9BB] text-[#63676C] hover:text-[#003399] rounded-none relative data-[state=active]:border-[#003399] data-[state=active]:text-[#003399] data-[state=active]:bg-transparent font-bold text-base"
              >
                {tab.title}
              </TabsTrigger>
            ))}
          </TabsList>
        </div>

        {faqData.map((tab) => (
          <TabsContent
            key={tab.value}
            value={tab.value}
            className="md:pt-6 md:pb-1"
          >
            <div className="space-y-6">
              {tab.faqs.map((faq) => (
                <div
                  key={faq.id}
                  className="hover:shadow-[0_1px_8px_rgba(0,0,0,0.1)] border border-[#E4E4E4] rounded-[8px] overflow-hidden"
                >
                  <button
                    onClick={() => toggleItem(faq.id)}
                    className="w-full flex items-center justify-between text-left transition-colors md:p-6 p-3"
                  >
                    <span className="text-[#454950] md:text-lg text-sm md:font-bold font-semibold leading-relaxed">
                      {faq.question}
                    </span>
                    <div className="flex-shrink-0">
                      {openItem === faq.id ? (
                        <Minus
                          strokeWidth={2}
                          className="w-6 h-6 text-[#63676C]"
                        />
                      ) : (
                        <Plus
                          strokeWidth={2}
                          className="w-6 h-6 text-[#63676C]"
                        />
                      )}
                    </div>
                  </button>
                  {/* Animated answer container with slide effect */}
                  <div
                    className="overflow-hidden transition-all duration-300 ease-in-out"
                    style={{
                      maxHeight: openItem === faq.id ? "500px" : "0px",
                      opacity: openItem === faq.id ? 1 : 0,
                    }}
                  >
                    <p className="text-[#454950] max-md:text-sm md:px-6 px-3 md:pb-6 pb-3">
                      {faq.answer}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
        ))}
      </Tabs>
    </main>
  );
};
export default FAQs;
